import { toast } from "sonner";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState } from "@/components/StateViews";
import { Loader2, Send, MessageSquare, User } from "lucide-react";

/**
 * Chat between a student and their assigned mentor (bidirectional).
 * Enforced server-side: /messages endpoints allow only student <-> assigned mentor.
 */
export default function Chat() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const endRef = useRef(null);
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/messages/partners");
        setPartners(data.partners || []);
        if (data.partners?.length) setActiveId(data.partners[0].id);
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadThread = useCallback(async ({ older = false } = {}) => {
    if (!activeId || document.visibilityState === "hidden") return;
    try {
      setError("");
      const before = older && messagesRef.current[0]?.created_at ? `?before=${encodeURIComponent(messagesRef.current[0].created_at)}` : "";
      const { data } = await api.get(`/messages/thread/${activeId}${before}`);
      setHasMore(Boolean(data.has_more));
      setMessages((current) => older ? [...(data.messages || []), ...current] : (data.messages || []));
      if (!older) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { setError(formatApiError(e)); }
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return undefined;
    loadThread();
    const poll = () => { if (document.visibilityState === "visible") loadThread(); };
    const timer = setInterval(poll, 20000);
    document.addEventListener("visibilitychange", poll);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", poll); };
  }, [activeId, loadThread]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post("/messages", { to_user_id: activeId, body: text });
      setText("");
      await loadThread();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (loading) return <DashboardLayout><div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div></DashboardLayout>;

  const activePartner = partners.find((p) => p.id === activeId);

  return (
    <DashboardLayout>
      <div data-testid="chat-page">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">التواصل</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">
            {user.role === "student" ? "الدردشة مع الموجّه" : "الدردشة مع الطلاب"}
          </h1>
          <p className="mt-2 text-slate-600 text-sm">التواصل الداخلي مقصور على الموجّه المخصّص لك (أو الطلاب المسندين إليك).</p>
        </div>

        {partners.length === 0 ? (
          <EmptyState icon={MessageSquare} title="لا يوجد جهات مراسلة" description={user.role === "student" ? "سيظهر الموجّه هنا بعد إسناده إليك من إدارة المنصة." : "سيظهر الطلاب هنا بعد إسنادهم إليك."} testid="chat-empty" />
        ) : (
          <div className="grid lg:grid-cols-4 gap-4 min-h-[560px]">
            <aside className="lg:col-span-1 rounded-2xl bg-white border border-cream-200 p-3 overflow-y-auto" data-testid="chat-partners">
              {partners.map((p) => (
                <button key={p.id} onClick={() => setActiveId(p.id)} className={`w-full text-start p-3 rounded-lg flex items-center gap-3 transition-colors ${p.id === activeId ? "bg-teal-50 border border-teal-200" : "hover:bg-slate-50"}`} data-testid={`chat-partner-${p.id}`}>
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-600 to-teal-900 grid place-items-center text-white font-bold shrink-0">{p.name?.[0] || <User className="h-5 w-5" />}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.role === "mentor" ? "موجّه" : "طالب"}</div>
                  </div>
                </button>
              ))}
            </aside>

            <div className="lg:col-span-3 rounded-2xl bg-white border border-cream-200 flex flex-col overflow-hidden">
              <div className="px-5 py-3 border-b border-cream-200 bg-cream-50">
                <div className="font-semibold text-slate-900">{activePartner?.name || "—"}</div>
              </div>
              <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-slate-50/40" data-testid="chat-messages">
                {error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center text-sm text-rose-800">{error}</div>}
                {hasMore && <button type="button" onClick={() => loadThread({ older: true })} className="mx-auto block text-xs font-bold text-teal-700 underline">تحميل الرسائل الأقدم</button>}
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-500 py-8">ابدأ المحادثة بإرسال أول رسالة.</div>
                ) : messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${mine ? "bg-teal-700 text-white" : "bg-white border border-cream-200 text-slate-900"}`}>
                        {m.body}
                        <div className={`text-[10px] mt-1 ${mine ? "text-teal-100" : "text-slate-400"}`} dir="ltr">
                          {new Date(m.created_at).toLocaleString("ar-SA")}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-cream-200 flex gap-2 bg-white">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="اكتب رسالتك..." className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" maxLength={4000} data-testid="chat-input" />
                <button type="submit" disabled={sending || !text.trim()} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="chat-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
